import { useEffect, useState } from 'react';
import { WorkerQueryFilters } from '../../models/worker';

export type WorkerLifecycleFilterValue = Pick<
    WorkerQueryFilters,
    | 'lastContactFrom'
    | 'lastContactTo'
    | 'residenceCountry'
    | 'residenceCity'
    | 'hasEndedPlacement'
    | 'minimumFeedbackRating'
    | 'maximumFeedbackRating'
    | 'easyflexAvailability'
    | 'workState'
    | 'missingRequiredFeedback'
    | 'cvCreated'
    | 'offeredToClient'
    | 'contacted'
>;

export function pickWorkerLifecycleFilters(filters: WorkerQueryFilters): WorkerLifecycleFilterValue {
    return {
        lastContactFrom: filters.lastContactFrom,
        lastContactTo: filters.lastContactTo,
        residenceCountry: filters.residenceCountry,
        residenceCity: filters.residenceCity,
        hasEndedPlacement: filters.hasEndedPlacement,
        minimumFeedbackRating: filters.minimumFeedbackRating,
        maximumFeedbackRating: filters.maximumFeedbackRating,
        easyflexAvailability: filters.easyflexAvailability,
        workState: filters.workState,
        missingRequiredFeedback: filters.missingRequiredFeedback,
        cvCreated: filters.cvCreated,
        offeredToClient: filters.offeredToClient,
        contacted: filters.contacted,
    };
}

export function summarizeWorkerLifecycleFilters(filters: WorkerLifecycleFilterValue): string[] {
    const parts: string[] = [];
    if (filters.lastContactFrom) parts.push(`contact from ${filters.lastContactFrom}`);
    if (filters.lastContactTo) parts.push(`contact to ${filters.lastContactTo}`);
    if (filters.residenceCountry) parts.push(`country ${filters.residenceCountry}`);
    if (filters.residenceCity) parts.push(`city ${filters.residenceCity}`);
    if (filters.minimumFeedbackRating !== undefined) parts.push(`rating at least ${filters.minimumFeedbackRating}`);
    if (filters.maximumFeedbackRating !== undefined) parts.push(`rating at most ${filters.maximumFeedbackRating}`);
    if (filters.easyflexAvailability) parts.push(filters.easyflexAvailability.replace(/_/g, ' ').toLowerCase());
    if (filters.workState) parts.push(filters.workState.replace(/_/g, ' ').toLowerCase());
    addBooleanSummary(parts, filters.hasEndedPlacement, 'has ended placement', 'no ended placement');
    addBooleanSummary(parts, filters.missingRequiredFeedback, 'missing required feedback', 'feedback complete');
    addBooleanSummary(parts, filters.cvCreated, 'CV created', 'CV not created');
    addBooleanSummary(parts, filters.offeredToClient, 'offered to client', 'not offered to client');
    addBooleanSummary(parts, filters.contacted, 'contacted', 'not contacted');
    return parts;
}

function addBooleanSummary(parts: string[], value: boolean | undefined, whenTrue: string, whenFalse: string) {
    if (value === true) parts.push(whenTrue);
    if (value === false) parts.push(whenFalse);
}

export function useSyncedWorkerLifecycleFilters(initialFilters: WorkerQueryFilters) {
    const serialized = JSON.stringify(pickWorkerLifecycleFilters(initialFilters));
    const [value, setValue] = useState<WorkerLifecycleFilterValue>(() => JSON.parse(serialized));
    useEffect(() => setValue(JSON.parse(serialized)), [serialized]);
    return [value, setValue] as const;
}
